// Уничтожение записи чёрного списка по требованию человека (тз 5.8.2).
//
// Модалка — ровно тот случай, ради которого она оставлена (правило 4): необратимое действие,
// которое надо подтвердить. Мягкого удаления здесь нет и быть не может: тз требует настоящего
// стирания персональных данных, и ради него в базе живёт отдельная функция с правами
// владельца — роли приложения удаление не выдано нигде.
//
// ## Окно называет ПОСЛЕДСТВИЯ, а не спрашивает «вы уверены»
//
// Уверенность человека не информация. Информация — что именно исчезнет, что останется и что
// после этого сверка перестанет находить этого человека: придёт наниматься снова — пропустят.
// Ровно это и есть цена решения, и знать её надо ДО нажатия.
//
// Основание обязательно и на сервере (minLength 3), и здесь. Проверяем на этой стороне не
// вместо сервера, а чтобы человек узнал об этом до отправки, а не отказом после.

import { useState } from 'react';

import { RiDeleteBinLine, RiLockLine } from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router';

import { parseSearch } from '@/shared/routing/search';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Modal from '@/shared/ui/modal';
import { TextField } from '@/shared/ui/text-field';

import type { BlacklistEntryOut } from '../api/blacklist';
import { useCan } from '../components/gates';
import { useEraseEntryMutation } from '../queries';
import { hiringHref } from '../route';

import { entryTitle } from '../views/BlacklistList';

/** Снятие и уничтожение делает администратор: право `hiring.blacklist.manage`. */
const MANAGE_PERMISSION = 'hiring.blacklist.manage';

/** Столько знаков основания требует сервер. Меньше — отказ, и лучше узнать это до отправки. */
const MIN_REASON = 3;

export function EraseDialog({ entry }: { entry: BlacklistEntryOut }) {
  const navigate = useNavigate();
  const location = useLocation();
  const allowed = useCan(MANAGE_PERMISSION);

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  const mutation = useEraseEntryMutation(entry.id);

  function confirm() {
    const основание = reason.trim();
    if (основание.length < MIN_REASON) {
      setReasonError(
        основание === ''
          ? 'Основание обязательно: стирание необратимо, и повод к нему остаётся в журнале навсегда.'
          : 'Основание короче трёх знаков не сохранится. Напишите фразой, а не одной буквой.',
      );
      return;
    }
    setReasonError(undefined);
    mutation.mutate(основание, {
      onSuccess: () => {
        setOpen(false);
        // Записи больше нет — вместе с персональными данными уходит и она сама. Оставить
        // человека на её карточке значит показать ему ошибку загрузки там, где он только что
        // получил ровно тот результат, которого добивался. Отбор и поиск при этом сохраняются:
        // уходит только выбранная строка.
        navigate(hiringHref('blacklist', parseSearch(location.search)), { replace: true });
      },
    });
  }

  return (
    <Modal.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          mutation.reset();
          setReasonError(undefined);
        }
      }}
    >
      <Modal.Trigger asChild>
        <Button.Root variant='error' mode='stroke' size='xsmall' type='button'>
          <Button.Icon as={RiDeleteBinLine} />
          Уничтожить
        </Button.Root>
      </Modal.Trigger>
      <Modal.Content>
        <Modal.Header
          icon={allowed ? RiDeleteBinLine : RiLockLine}
          title={allowed ? 'Уничтожить запись?' : 'Уничтожить запись нельзя'}
          // Имя стоит отдельной фразой, а не внутри вопроса: по-русски «Уничтожить Старцев
          // Иван Петрович?» — это именительный падеж там, где нужен винительный, а склонять
          // фамилии в коде нельзя, женские и несклоняемые дают ошибку чаще, чем верный ответ.
          description={
            allowed
              ? `${entryTitle(entry)}. Действие необратимо: обратной кнопки у стирания нет.`
              : 'Стирание персональных данных выполняет администратор системы — право на него есть только у него.'
          }
        />
        <Modal.Body>
          {allowed ? (
            <div className='flex flex-col gap-3'>
              <ul className='flex flex-col gap-1.5 text-paragraph-sm text-text-sub-600'>
                <li>
                  Исчезнут фамилия, имя и отчество, дата рождения, телефон, серия и номер паспорта,
                  номер удостоверения охранника.
                </li>
                <li>
                  Исчезнет и сама запись вместе с причиной, правовым основанием и сроком хранения: в
                  чёрном списке её больше не будет.
                </li>
                <li>
                  Останется след в журнале стираний: что уничтожено, когда, кем и на каком
                  основании. Он не стирается никогда.
                </li>
                <li>
                  Сверка перестанет находить этого человека. Придёт наниматься снова — совпадения не
                  будет, и его пропустят как незнакомого.
                </li>
              </ul>
              {/* Снятие рядом названо намеренно: половина обращений «удалите его из списка»
                  на деле про снятие — человек рассчитался и должен снова проходить найм.
                  Стирание нужно только по требованию самого человека о его данных. */}
              <p className='text-paragraph-xs text-text-sub-600'>
                Если человека просто надо вернуть к найму, запись не уничтожают, а снимают: тогда
                причина и история остаются на месте.
              </p>
              <TextField
                id='erase-reason'
                label='Основание уничтожения'
                required
                value={reason}
                onChange={setReason}
                placeholder='Например: заявление человека об удалении персональных данных'
                error={reasonError}
                hint='Останется в журнале стираний навсегда — это единственное, что от записи уцелеет.'
              />
            </div>
          ) : (
            <p className='text-paragraph-sm text-text-sub-600'>
              У вашей учётной записи нет права на стирание персональных данных, поэтому кнопка
              ничего не изменит. Попросите администратора выполнить это — либо, если человека надо
              просто вернуть к найму, снимите запись: на снятие право шире.
            </p>
          )}
          {mutation.isError && <ErrorAlert error={mutation.error} className='mt-3' />}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button.Root variant='neutral' mode='stroke' size='small' className='w-full'>
              {allowed ? 'Отмена' : 'Понятно'}
            </Button.Root>
          </Modal.Close>
          {allowed && (
            <Button.Root
              variant='error'
              mode='filled'
              size='small'
              className='w-full'
              type='button'
              onClick={confirm}
            >
              {mutation.isPending ? 'Уничтожаем…' : 'Уничтожить'}
            </Button.Root>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
